import React from 'react';
interface SlideTwoProps {
  onNext: () => void;
  onBack: () => void;
  onCTA: () => void;
  selectedOption: string | null;
}
const POLL_RESULTS = [{
  label: 'Spend more time på fjellet 🏔️',
  key: 'Spend more time på fjellet 🏔️',
  pct: 42
}, {
  label: 'Add one more patient',
  key: 'Add one more patient',
  pct: 23
}, {
  label: 'Stay on schedule',
  key: 'Stay on schedule',
  pct: 35
}];
interface OptionVariant {
  image: string;
  imageAlt: string;
  headline: string;
  body: string;
}
const VARIANTS: Record<string, OptionVariant> = {
  'Spend more time på fjellet 🏔️': {
    image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?auto=format&fit=crop&w=800&q=80',
    imageAlt: 'Person cross-country skiing in Norwegian mountains',
    headline: 'Your peers agree — more time for the moments that matter',
    body: "What if bonding took 2 minutes instead of 20? That's 18 minutes back — enough to leave on time and hit the trails."
  },
  'Add one more patient': {
    image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=800&q=80',
    imageAlt: 'Dentist treating a patient in a modern dental clinic',
    headline: 'Your peers agree — more capacity without more stress',
    body: 'With 18 minutes saved per bonding appointment, you could see one additional patient daily — without rushing anyone.'
  },
  'Stay on schedule': {
    image: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?auto=format&fit=crop&w=800&q=80',
    imageAlt: 'Minimal clock representing perfect timing and efficiency',
    headline: 'Your peers agree — staying on time matters most',
    body: 'When bonding takes 2 minutes instead of 20, staying on schedule stops being a goal — it becomes the default.'
  }
};
const DEFAULT_VARIANT = VARIANTS['Stay on schedule'];
export function SlideTwo({
  onNext,
  onBack,
  onCTA,
  selectedOption
}: SlideTwoProps) {
  const variant = selectedOption && VARIANTS[selectedOption] || DEFAULT_VARIANT;
  return <div className="flex flex-col h-full w-full justify-center" data-id="element-155">
      {/* Back button */}
      <button onClick={onBack} className="self-start mb-4 flex items-center text-solventum-secondary hover:text-white transition-colors duration-200 text-sm font-body group" aria-label="Go back" data-id="element-156">
        <svg className="w-4 h-4 mr-1.5 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-157">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" data-id="element-158" />
        </svg>
        Back
      </button>

      {/* Headline */}
      <div className="animate-fade-in-up opacity-0-init mb-5" style={{
      animationDelay: '0ms'
    }} data-id="element-159">
        <h2 className="text-2xl md:text-3xl font-extrabold text-white leading-tight text-center" data-id="element-160">
          {variant.headline}
        </h2>
      </div>

      {/* Poll Results Visualization */}
      <div className="animate-fade-in-up opacity-0-init bg-white/5 border border-white/10 rounded-2xl p-5 mb-5" style={{
      animationDelay: '200ms'
    }} data-id="element-161">
        <p className="text-solventum-secondary text-xs font-body uppercase tracking-wider mb-4" data-id="element-162">
          Poll results
        </p>
        <div className="space-y-3" data-id="element-163">
          {POLL_RESULTS.map(item => {
          const isUserChoice = item.key === selectedOption;
          return <div key={item.key} data-id="element-164">
                <div className="flex justify-between items-center mb-1" data-id="element-165">
                  <span className={`text-sm font-body ${isUserChoice ? 'text-solventum-primary font-bold' : 'text-white/80'}`} data-id="element-166">
                    {item.label} {isUserChoice && '← you'}
                  </span>
                  <span className={`text-sm font-headline font-bold ${isUserChoice ? 'text-solventum-primary' : 'text-white/60'}`} data-id="element-167">
                    {item.pct}%
                  </span>
                </div>
                <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden" data-id="element-168">
                  <div className={`h-full rounded-full transition-all duration-1000 ease-out ${isUserChoice ? 'bg-solventum-primary' : 'bg-solventum-secondary/50'}`} style={{
                width: `${item.pct}%`,
                animationDelay: '400ms'
              }} data-id="element-169" />
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* Hero image */}
      <div className="animate-fade-in-up opacity-0-init w-full mb-5 rounded-2xl overflow-hidden relative" style={{
      animationDelay: '400ms'
    }} data-id="element-170">
        <div className="aspect-[16/9] w-full rounded-2xl overflow-hidden bg-white/5" data-id="element-171">
          <img src={variant.image} alt={variant.imageAlt} className="w-full h-full object-cover" data-id="element-172" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-solventum-bg to-transparent" data-id="element-173" />
      </div>

      {/* Body text */}
      <div className="animate-fade-in-up opacity-0-init mb-6" style={{
      animationDelay: '600ms'
    }} data-id="element-174">
        <p className="text-lg text-white/90 font-body leading-relaxed text-center" data-id="element-175">
          {variant.body}
        </p>
      </div>

      {/* CTAs */}
      <div className="animate-fade-in-up opacity-0-init flex flex-col gap-3" style={{
      animationDelay: '800ms'
    }} data-id="element-176">
        <button onClick={onCTA} className="w-full bg-solventum-primary hover:bg-[#04c444] text-solventum-bg font-headline font-extrabold py-4 px-6 rounded-xl transition-colors duration-200 text-lg shadow-[0_0_15px_rgba(5,221,77,0.2)]" data-id="element-177">
          👉 See how this could be you →
        </button>
        <button onClick={onNext} className="w-full bg-white/5 border border-white/15 hover:border-white/30 text-white font-headline font-bold py-3 px-6 rounded-xl transition-all duration-200 text-base" data-id="element-178">
          Next →
        </button>
      </div>
    </div>;
}